import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

// ─────────────────────────────────────────
// COLORES POR ÁREA
// ─────────────────────────────────────────
const COLORES_AREA = [
  '#1a1a2e', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6',
];

const colorPorId = (id) => COLORES_AREA[id % COLORES_AREA.length];

const avatarIniciales = (nombre = '') =>
  nombre.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

// ─────────────────────────────────────────
// FORMATEAR FECHA EN SEPARADOR
// ─────────────────────────────────────────
const fechaMensaje = (fecha) => {
  const d = new Date(fecha);
  if (isToday(d)) return 'Hoy';
  if (isYesterday(d)) return 'Ayer';
  return format(d, "d 'de' MMMM", { locale: es });
};

// ─────────────────────────────────────────
// BURBUJA DE MENSAJE
// ─────────────────────────────────────────
function BurbujaMensaje({ mensaje, esMio, mostrarAvatar }) {
  const hora = format(new Date(mensaje.created_at), 'HH:mm');

  return (
    <div style={{
      ...bm.wrapper,
      justifyContent: esMio ? 'flex-end' : 'flex-start',
      marginBottom: mostrarAvatar ? 12 : 3,
    }}>
      {/* Avatar lado izquierdo */}
      {!esMio && (
        <div style={{ width: 32, flexShrink: 0 }}>
          {mostrarAvatar && (
            <div style={{
              ...bm.avatar,
              background: colorPorId(mensaje.usuario_id || 0),
            }}>
              {avatarIniciales(mensaje.usuario_nombre)}
            </div>
          )}
        </div>
      )}

      <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Nombre + área (solo primer msg del grupo) */}
        {!esMio && mostrarAvatar && (
          <div style={bm.nombreRow}>
            <span style={{ ...bm.nombre, color: colorPorId(mensaje.usuario_id || 0) }}>
              {mensaje.usuario_nombre}
            </span>
            {mensaje.area_nombre && (
              <span style={bm.area}>{mensaje.area_nombre}</span>
            )}
          </div>
        )}

        {/* Burbuja */}
        <div style={{
          ...bm.burbuja,
          background: esMio ? '#1a1a2e' : 'white',
          color: esMio ? 'white' : '#111827',
          borderRadius: esMio
            ? (mostrarAvatar ? '18px 18px 4px 18px' : '18px 4px 4px 18px')
            : (mostrarAvatar ? '18px 18px 18px 4px' : '4px 18px 18px 18px'),
          boxShadow: esMio
            ? '0 2px 8px rgba(26,26,46,0.25)'
            : '0 1px 4px rgba(0,0,0,0.08)',
        }}>
          <span style={bm.texto}>{mensaje.mensaje}</span>
          <span style={{ ...bm.hora, color: esMio ? 'rgba(255,255,255,0.5)' : '#9CA3AF' }}>
            {hora}
          </span>
        </div>
      </div>

      {/* Avatar lado derecho (mis mensajes) */}
      {esMio && (
        <div style={{ width: 32, flexShrink: 0 }}>
          {mostrarAvatar && (
            <div style={{ ...bm.avatar, background: '#1a1a2e' }}>
              {avatarIniciales(mensaje.usuario_nombre)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const bm = {
  wrapper: { display: 'flex', alignItems: 'flex-end', gap: 8, paddingInline: 16 },
  avatar: {
    width: 32, height: 32, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 800, color: 'white', flexShrink: 0,
  },
  nombreRow: { display: 'flex', alignItems: 'baseline', gap: 6, paddingLeft: 4 },
  nombre: { fontSize: 12, fontWeight: 700 },
  area: { fontSize: 11, color: '#9CA3AF' },
  burbuja: {
    padding: '9px 14px', display: 'flex', alignItems: 'flex-end',
    gap: 8, flexWrap: 'wrap',
  },
  texto: { fontSize: 14, lineHeight: 1.45, wordBreak: 'break-word', flex: 1 },
  hora: { fontSize: 10, flexShrink: 0, alignSelf: 'flex-end', marginBottom: 1 },
};

// ─────────────────────────────────────────
// SEPARADOR DE FECHA
// ─────────────────────────────────────────
function SeparadorFecha({ fecha }) {
  return (
    <div style={sf.wrapper}>
      <div style={sf.linea} />
      <span style={sf.label}>{fechaMensaje(fecha)}</span>
      <div style={sf.linea} />
    </div>
  );
}

const sf = {
  wrapper: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px' },
  linea: { flex: 1, height: 1, background: '#F3F4F6' },
  label: { fontSize: 11, fontWeight: 700, color: '#9CA3AF', whiteSpace: 'nowrap', background: '#F8FAFC', padding: '3px 10px', borderRadius: 20, border: '1px solid #F3F4F6' },
};

// ─────────────────────────────────────────
// ITEM DE CANAL
// ─────────────────────────────────────────
function ItemCanal({ canal, activo, onClick, noLeidos }) {
  const iconos = { general: '📢', area: '👥', directo: '💬' };
  return (
    <button onClick={() => onClick(canal)} style={{
      ...ic.btn,
      background: activo ? '#EFF6FF' : 'transparent',
      borderLeft: activo ? '3px solid #3B82F6' : '3px solid transparent',
    }}>
      <span style={ic.icon}>{iconos[canal.tipo] || '💬'}</span>
      <span style={{ ...ic.nombre, fontWeight: noLeidos > 0 ? 700 : 500 }}>{canal.nombre}</span>
      {noLeidos > 0 && (
        <span style={ic.badge}>{noLeidos > 99 ? '99+' : noLeidos}</span>
      )}
    </button>
  );
}

const ic = {
  btn: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '10px 14px', border: 'none', borderRadius: 8, cursor: 'pointer',
    textAlign: 'left', transition: 'all 0.12s',
  },
  icon: { fontSize: 16, flexShrink: 0 },
  nombre: { flex: 1, fontSize: 14, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: { background: '#EF4444', color: 'white', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 20, flexShrink: 0 },
};

// ─────────────────────────────────────────
// ÁREA DE MENSAJES
// ─────────────────────────────────────────
function AreaMensajes({ mensajes, usuarioId, cargando }) {
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  if (cargando) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>
      Cargando mensajes...
    </div>
  );

  if (mensajes.length === 0) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: '#9CA3AF' }}>
      <span style={{ fontSize: 40 }}>💬</span>
      <p style={{ margin: 0 }}>Aún no hay mensajes. ¡Sé el primero!</p>
    </div>
  );

  // Agrupar mensajes por usuario consecutivo y agregar separadores de fecha
  const elementos = [];
  let ultimaFecha = null;
  let ultimoUsuarioId = null;
  let ultimoTiempo = null;

  mensajes.forEach((msg, i) => {
    const fechaActual = new Date(msg.created_at);

    // Separador de fecha
    if (!ultimaFecha || !isSameDay(ultimaFecha, fechaActual)) {
      elementos.push({ tipo: 'fecha', fecha: msg.created_at, key: `fecha-${i}` });
      ultimaFecha = fechaActual;
      ultimoUsuarioId = null;
    }

    // Nuevo grupo si cambia usuario o han pasado más de 5 minutos
    const tiempoTranscurrido = ultimoTiempo ? (fechaActual - ultimoTiempo) / 1000 / 60 : 99;
    const esNuevoGrupo = msg.usuario_id !== ultimoUsuarioId || tiempoTranscurrido > 5;

    elementos.push({
      tipo: 'mensaje',
      msg,
      esMio: msg.usuario_id === usuarioId,
      mostrarAvatar: esNuevoGrupo,
      key: `msg-${msg.id}`,
    });

    ultimoUsuarioId = msg.usuario_id;
    ultimoTiempo = fechaActual;
  });

  return (
    <div style={{ flex: 1, overflowY: 'auto', paddingTop: 16, paddingBottom: 8 }}>
      {elementos.map(el => {
        if (el.tipo === 'fecha') {
          return <SeparadorFecha key={el.key} fecha={el.fecha} />;
        }
        return (
          <BurbujaMensaje
            key={el.key}
            mensaje={el.msg}
            esMio={el.esMio}
            mostrarAvatar={el.mostrarAvatar}
          />
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

// ─────────────────────────────────────────
// INPUT DE MENSAJE
// ─────────────────────────────────────────
function InputMensaje({ onEnviar, disabled }) {
  const [texto, setTexto] = useState('');
  const inputRef = useRef(null);

  const enviar = () => {
    const limpio = texto.trim();
    if (!limpio || disabled) return;
    onEnviar(limpio);
    setTexto('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <div style={im.wrapper}>
      <textarea
        ref={inputRef}
        value={texto}
        onChange={e => setTexto(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Escribe un mensaje... (Enter para enviar, Shift+Enter para nueva línea)"
        rows={1}
        disabled={disabled}
        style={{
          ...im.input,
          height: texto.split('\n').length > 1 ? 'auto' : 44,
          minHeight: 44,
        }}
      />
      <button
        onClick={enviar}
        disabled={!texto.trim() || disabled}
        style={{
          ...im.btn,
          opacity: (!texto.trim() || disabled) ? 0.4 : 1,
        }}
      >
        ➤
      </button>
    </div>
  );
}

const im = {
  wrapper: {
    display: 'flex', alignItems: 'flex-end', gap: 10,
    padding: '12px 16px', background: 'white',
    borderTop: '1px solid #F3F4F6',
  },
  input: {
    flex: 1, padding: '11px 16px', border: '2px solid #E5E7EB',
    borderRadius: 22, fontSize: 14, resize: 'none', outline: 'none',
    fontFamily: 'inherit', lineHeight: 1.4, maxHeight: 120,
    transition: 'border-color 0.15s', boxSizing: 'border-box',
  },
  btn: {
    width: 44, height: 44, borderRadius: '50%', border: 'none',
    background: '#1a1a2e', color: 'white', fontSize: 18,
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, transition: 'opacity 0.15s',
  },
};

// ─────────────────────────────────────────
// PÁGINA PRINCIPAL: CHAT
// ─────────────────────────────────────────
export default function Chat() {
  const { usuario, token } = useAuth();

  const [canales, setCanales]       = useState([]);
  const [canalActivo, setCanalActivo] = useState(null);
  const [mensajes, setMensajes]     = useState([]);
  const [noLeidos, setNoLeidos]     = useState({});
  const [cargandoMsgs, setCargandoMsgs] = useState(false);
  const [socketConectado, setSocketConectado] = useState(false);
  const socketRef = useRef(null);

  // ── Conectar socket
  useEffect(() => {
    if (!token) return;

    const socket = io('/', {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      setSocketConectado(true);
    });

    socket.on('disconnect', () => {
      setSocketConectado(false);
    });

    socket.on('nuevo_mensaje', (msg) => {
      setMensajes(prev => {
        // Evitar duplicados
        if (prev.find(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });

      // Contar no leídos si no estamos en ese canal
      if (msg.canal_id !== canalActivo?.id) {
        setNoLeidos(prev => ({
          ...prev,
          [msg.canal_id]: (prev[msg.canal_id] || 0) + 1,
        }));
      }
    });

    socket.on('alerta_urgente', (data) => {
      toast.custom(() => (
        <div style={{ background: '#FEF2F2', border: '2px solid #EF4444', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'center', boxShadow: '0 4px 20px rgba(239,68,68,0.3)' }}>
          <span style={{ fontSize: 24 }}>🚨</span>
          <div>
            <strong style={{ color: '#991B1B' }}>NOVEDAD URGENTE</strong>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151' }}>{data.titulo} — {data.de}</p>
          </div>
        </div>
      ), { duration: 8000 });
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
    };
  }, [token]);

  // ── Actualizar listener del canal activo
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !canalActivo) return;
    socket.emit('unirse_canal', canalActivo.id);
  }, [canalActivo]);

  // ── Cargar canales
  useEffect(() => {
    API.get('/canales').then(r => {
      setCanales(r.data);
      if (r.data.length > 0) abrirCanal(r.data[0]);
    }).catch(() => {
      // Si el endpoint no existe aún, crear canales localmente para demo
      const canalesDemo = [
        { id: 1, nombre: 'General', tipo: 'general' },
        { id: 2, nombre: 'Administración', tipo: 'area' },
        { id: 3, nombre: 'Producción', tipo: 'area' },
        { id: 4, nombre: 'Puntos de Venta', tipo: 'area' },
        { id: 5, nombre: 'Gerencia', tipo: 'area' },
      ];
      setCanales(canalesDemo);
      abrirCanal(canalesDemo[0]);
    });
  }, []);

  // ── Abrir canal y cargar histórico
  const abrirCanal = useCallback((canal) => {
    setCanalActivo(canal);
    setCargandoMsgs(true);
    setMensajes([]);

    // Limpiar no leídos de este canal
    setNoLeidos(prev => ({ ...prev, [canal.id]: 0 }));

    API.get(`/chat/mensajes/${canal.id}`)
      .then(r => setMensajes(r.data || []))
      .catch(() => setMensajes([]))
      .finally(() => setCargandoMsgs(false));
  }, []);

  // ── Enviar mensaje
  const enviarMensaje = useCallback((texto) => {
    if (!canalActivo || !socketRef.current) return;

    const mensajeOptimista = {
      id: `temp-${Date.now()}`,
      canal_id: canalActivo.id,
      usuario_id: usuario.id,
      usuario_nombre: usuario.nombre,
      area_nombre: usuario.area_nombre,
      mensaje: texto,
      tipo: 'texto',
      created_at: new Date().toISOString(),
    };

    // Mostrar optimistamente
    setMensajes(prev => [...prev, mensajeOptimista]);

    socketRef.current.emit('mensaje', {
      canal_id: canalActivo.id,
      mensaje: texto,
      tipo: 'texto',
    });
  }, [canalActivo, usuario]);

  const totalNoLeidos = Object.values(noLeidos).reduce((a, b) => a + b, 0);

  return (
    <div style={pg.root}>
      {/* ── Sidebar canales ── */}
      <div style={pg.sidebar}>
        <div style={pg.sidebarHeader}>
          <h2 style={pg.sidebarTitulo}>💬 Chat</h2>
          <div style={{ ...pg.estadoSocket, background: socketConectado ? '#D1FAE5' : '#FEE2E2' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: socketConectado ? '#10B981' : '#EF4444', display: 'inline-block' }} />
            {socketConectado ? 'Conectado' : 'Desconectado'}
          </div>
        </div>

        <div style={pg.sidebarUsuario}>
          <div style={{ ...pg.myAvatar, background: colorPorId(usuario?.id || 0) }}>
            {avatarIniciales(usuario?.nombre)}
          </div>
          <div>
            <div style={pg.myNombre}>{usuario?.nombre}</div>
            <div style={pg.myArea}>{usuario?.area_nombre}</div>
          </div>
        </div>

        <div style={pg.canalesLabel}>Canales</div>

        <div style={pg.canalesList}>
          {canales.map(c => (
            <ItemCanal
              key={c.id}
              canal={c}
              activo={canalActivo?.id === c.id}
              onClick={abrirCanal}
              noLeidos={noLeidos[c.id] || 0}
            />
          ))}
        </div>

        {/* Info área */}
        <div style={pg.sidebarFooter}>
          <div style={pg.areaInfo}>
            <div style={pg.areaInfoLabel}>Tu área</div>
            <div style={pg.areaInfoValor}>{usuario?.area_nombre || '—'}</div>
          </div>
        </div>
      </div>

      {/* ── Área de chat ── */}
      <div style={pg.chatArea}>
        {canalActivo ? (
          <>
            {/* Header del canal */}
            <div style={pg.chatHeader}>
              <div style={pg.chatHeaderInfo}>
                <span style={pg.chatHeaderIcon}>
                  {{ general: '📢', area: '👥', directo: '💬' }[canalActivo.tipo] || '💬'}
                </span>
                <div>
                  <div style={pg.chatHeaderNombre}>{canalActivo.nombre}</div>
                  <div style={pg.chatHeaderSub}>
                    {canalActivo.descripcion || ({ general: 'Canal para toda la organización', area: 'Canal de área', directo: 'Mensaje directo' }[canalActivo.tipo])}
                  </div>
                </div>
              </div>

              {!socketConectado && (
                <div style={pg.offlineBanner}>
                  ⚠️ Sin conexión — Los mensajes pueden no enviarse
                </div>
              )}
            </div>

            {/* Mensajes */}
            <AreaMensajes
              mensajes={mensajes}
              usuarioId={usuario?.id}
              cargando={cargandoMsgs}
            />

            {/* Input */}
            <InputMensaje
              onEnviar={enviarMensaje}
              disabled={!socketConectado}
            />
          </>
        ) : (
          <div style={pg.placeholder}>
            <span style={{ fontSize: 56 }}>💬</span>
            <h3 style={{ margin: '16px 0 8px', color: '#374151' }}>Bienvenido al Chat</h3>
            <p style={{ color: '#9CA3AF', margin: 0 }}>Selecciona un canal para comenzar</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// ENDPOINT NECESARIO: agregar al backend
// GET /api/chat/mensajes/:canal_id
// ─────────────────────────────────────────
// Asegúrate de tener este endpoint en el backend:
//
// router.get('/chat/mensajes/:canal_id', verificarToken, async (req, res) => {
//   const { canal_id } = req.params;
//   const result = await pool.query(
//     `SELECT m.*, u.nombre as usuario_nombre, a.nombre as area_nombre
//      FROM mensajes_chat m
//      JOIN usuarios u ON m.usuario_id = u.id
//      LEFT JOIN areas a ON u.area_id = a.id
//      WHERE m.canal_id = $1
//      ORDER BY m.created_at ASC
//      LIMIT 100`, [canal_id]
//   );
//   res.json(result.rows);
// });
//
// router.get('/canales', verificarToken, async (req, res) => {
//   const result = await pool.query(
//     'SELECT * FROM canales_chat WHERE activo = TRUE ORDER BY tipo DESC, nombre'
//   );
//   res.json(result.rows);
// });

const pg = {
  root: {
    display: 'flex',
    height: 'calc(100vh - 56px)',
    fontFamily: "'Segoe UI', sans-serif",
    background: '#F8FAFC',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
    margin: '-28px',
  },
  sidebar: {
    width: 260, background: 'white', borderRight: '1px solid #F3F4F6',
    display: 'flex', flexDirection: 'column', flexShrink: 0,
  },
  sidebarHeader: {
    padding: '20px 16px 12px', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', borderBottom: '1px solid #F3F4F6',
  },
  sidebarTitulo: { margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' },
  estadoSocket: {
    padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: 5, color: '#374151',
  },
  sidebarUsuario: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 16px', borderBottom: '1px solid #F3F4F6',
  },
  myAvatar: {
    width: 38, height: 38, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 800, color: 'white', flexShrink: 0,
  },
  myNombre: { fontSize: 13, fontWeight: 700, color: '#111827' },
  myArea: { fontSize: 11, color: '#9CA3AF' },
  canalesLabel: {
    padding: '12px 16px 6px', fontSize: 10, fontWeight: 800,
    color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  canalesList: { flex: 1, padding: '0 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 },
  sidebarFooter: { padding: '14px 16px', borderTop: '1px solid #F3F4F6' },
  areaInfo: { display: 'flex', flexDirection: 'column', gap: 2 },
  areaInfoLabel: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' },
  areaInfoValor: { fontSize: 13, fontWeight: 600, color: '#374151' },
  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#F8FAFC' },
  chatHeader: {
    padding: '16px 20px', background: 'white', borderBottom: '1px solid #F3F4F6',
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  chatHeaderInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  chatHeaderIcon: { fontSize: 24 },
  chatHeaderNombre: { fontSize: 16, fontWeight: 800, color: '#111827' },
  chatHeaderSub: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  offlineBanner: {
    background: '#FEF3C7', color: '#92400E', padding: '6px 14px',
    borderRadius: 8, fontSize: 12, fontWeight: 600,
  },
  placeholder: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', color: '#9CA3AF',
  },
};
